import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Tabs,
    Tab,
    TextField,
    Typography,
    MenuItem,
    Button,
    Divider,
    Card,
    CardContent,
    IconButton,
    CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { ArrowBack } from "@mui/icons-material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../layouts/main/main_layout";
import HTMLEditor from "../../../components/html_editor/html_editor";
import locales from "../../../constants/locales";
import { moduleTypes } from "../constants/landing-page-constants";
import SnackbarComp from "./snackbar";
import { ConfirmLanguagesDialog } from "./confirm_languages_dialog";
import { MediaUploadEdit } from "./media-upload-edit";
import { getSignedUrl, updateLandingPage } from "../../../services/landing_page/landing_page";

type LanguageFormData = {
    title: string;
    description: string;
};

type SharedFormData = {
    mediaType?: "image" | "tts" | "audio" | "video" | null;
    mediaFile?: FileList | string;
    module?: string;
    module_id?: string;
    submodule_id?: string;
    content_name?: string;
};

type ExistingMediaByLang = Record<
    string,
    {
        image?: string;
        tts?: string;
        audio?: string;
        video?: string;
        media_url?: string;
    }
>;

export const EditLandingPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const landingPageData = (location.state as any)?.landingPageData || null;

    const exceptedModules = ["HOSPITAL", "NEEDS", "PROFILE", "REFERRAL", "HOMEPAGE"];

    const [tabIndex, setTabIndex] = useState(0);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);
    const [snackbar, setSnackbar] = useState<any>({ open: false, message: "", severity: "success" });

    // New uploads (only if user replaces media)
    const [ttsFilesByLanguage, setTtsFilesByLanguage] = useState<Record<string, File>>({});
    const [audioFilesByLanguage, setAudioFilesByLanguage] = useState<Record<string, File>>({});
    const [videoFilesByLanguage, setVideoFilesByLanguage] = useState<Record<string, File>>({});
    const [imageFilesByLanguage, setImageFilesByLanguage] = useState<Record<string, File>>({});
    // Separate image file for audio/TTS landing pages (notification-style) - per language
    const [separateImageFileByLanguage, setSeparateImageFileByLanguage] = useState<Record<string, File | string | undefined>>({});

    const [ObjectId, setObjectId] = useState<number | null>(null);
    const [existingMediaByLang, setExistingMediaByLang] = useState<ExistingMediaByLang>({});

    const [sharedData, setSharedData] = useState<SharedFormData>({
        mediaType: null,
        mediaFile: undefined,
        module: "",
        module_id: "",
        submodule_id: "",
        content_name: "",
    });

    const [formData, setFormData] = useState<Record<string, LanguageFormData>>(
        locales.reduce((acc, lang) => {
            acc[lang.code] = { title: "", description: "" };
            return acc;
        }, {} as Record<string, LanguageFormData>)
    );

    const { control, reset, watch, getValues } = useForm<LanguageFormData>({
        defaultValues: { title: "", description: "" },
    });

    const { control: sharedControl, reset: resetSharedForm } = useForm<any>({
        defaultValues: {
            module: "",
            module_id: "",
            submodule_id: "",
            mediaType: null,
            mediaFile: "",
            content_name: "",
        },
    });

    const currentLang = locales[tabIndex].code;
    const watchedTitle = watch("title");
    const watchedDescription = watch("description");

    const isTabSwitchingRef = useRef(false);
    const formDataRef = useRef<Record<string, LanguageFormData>>(formData);
    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    const contentId = useMemo(() => {
        const numeric = Number(id);
        if (!id) return null;
        return Number.isFinite(numeric) ? numeric : id;
    }, [id]);

    const handleMediaUploadError = (message: string) => {
        setSnackbar({ open: true, message, severity: "error" });
    };

    const handleMediaTypeChange = (type: "image" | "tts" | "audio" | "video" | null | undefined) => {
        setSharedData((prev) => ({ ...prev, mediaType: type || null, mediaFile: undefined }));
        // clear staged uploads for other types
        if (type !== "audio") setAudioFilesByLanguage({});
        if (type !== "video") setVideoFilesByLanguage({});
        if (type !== "tts") setTtsFilesByLanguage({});
        if (type !== "image") setImageFilesByLanguage({});
        if (type !== "audio" && type !== "tts") setSeparateImageFileByLanguage({});
        
        // Clear existingMediaByLang to prevent old media from showing when switching tabs
        // Filter to only keep media that matches the new media type
        if (type) {
            setExistingMediaByLang((prev) => {
                const filtered: ExistingMediaByLang = {};
                Object.keys(prev).forEach((lang) => {
                    const media = prev[lang];
                    const filteredMedia: any = {};
                    
                    // Only keep media that matches the new type
                    if (type === "image" && media.image) {
                        filteredMedia.image = media.image;
                    } else if (type === "tts" && media.tts) {
                        filteredMedia.tts = media.tts;
                        if (media.image) filteredMedia.image = media.image; // Keep secondary image for TTS
                    } else if (type === "audio" && (media.audio || (media.media_url && /audio_/i.test(media.media_url)))) {
                        filteredMedia.audio = media.audio;
                        filteredMedia.media_url = media.media_url;
                        if (media.image) filteredMedia.image = media.image; // Keep secondary image for audio
                    } else if (type === "video" && (media.video || (media.media_url && /video_/i.test(media.media_url)))) {
                        filteredMedia.video = media.video;
                        filteredMedia.media_url = media.media_url;
                    }
                    
                    // Only add to filtered if there's matching media
                    if (Object.keys(filteredMedia).length > 0) {
                        filtered[lang] = filteredMedia;
                    }
                });
                return filtered;
            });
        } else {
            // If type is null, clear all existing media
            setExistingMediaByLang({});
        }
    };

    const handleMediaFileChange = (file: FileList | null) => {
        setSharedData((prev) => ({ ...prev, mediaFile: file || undefined }));
    };

    const handleSeparateImageFileChange = (language: string, file: File | null) => {
        if (file === null) {
            setSeparateImageFileByLanguage(prev => {
                const updated = { ...prev };
                delete updated[language];
                return updated;
            });
        } else {
            setSeparateImageFileByLanguage(prev => ({
                ...prev,
                [language]: file,
            }));
        }
    };

    const handleImageFileChange = (language: string, file: File) => {
        setImageFilesByLanguage((prev) => ({ ...prev, [language]: file }));
    };

    // Create a FileList from a single File (for passing new upload to MediaUploadEdit)
    const fileToFileList = (file: File): FileList => {
        const dt = new DataTransfer();
        dt.items.add(file);
        return dt.files;
    };

    const setMediaForLanguage = (langCode: string, mediaType: SharedFormData["mediaType"], perLang: ExistingMediaByLang) => {
        if (!mediaType) {
            setSharedData((prev) => ({ ...prev, mediaType: null, mediaFile: undefined }));
            setSeparateImageFileByLanguage(prev => {
                const updated = { ...prev };
                delete updated[langCode];
                return updated;
            });
            return;
        }

        const m = perLang?.[langCode] || {};
        if (mediaType === "image") {
            const mediaFileToSet = imageFilesByLanguage[langCode]
                ? fileToFileList(imageFilesByLanguage[langCode])
                : (m.image || undefined);
            setSharedData((prev) => ({ ...prev, mediaType, mediaFile: mediaFileToSet }));
            setSeparateImageFileByLanguage(prev => {
                const updated = { ...prev };
                delete updated[langCode];
                return updated;
            });
            return;
        }
        if (mediaType === "tts") {
            const mediaFileToSet = ttsFilesByLanguage[langCode]
                ? fileToFileList(ttsFilesByLanguage[langCode])
                : (m.tts || undefined);
            setSharedData((prev) => ({ ...prev, mediaType, mediaFile: mediaFileToSet }));
            setSeparateImageFileByLanguage(prev => ({
                ...prev,
                [langCode]: prev[langCode] ?? m.image ?? undefined,
            }));
            return;
        }
        if (mediaType === "audio") {
            const mediaFileToSet = audioFilesByLanguage[langCode]
                ? fileToFileList(audioFilesByLanguage[langCode])
                : (m.audio || m.media_url || undefined);
            setSharedData((prev) => ({ ...prev, mediaType, mediaFile: mediaFileToSet }));
            setSeparateImageFileByLanguage(prev => ({
                ...prev,
                [langCode]: prev[langCode] ?? m.image ?? undefined,
            }));
            return;
        }
        if (mediaType === "video") {
            const mediaFileToSet = videoFilesByLanguage[langCode]
                ? fileToFileList(videoFilesByLanguage[langCode])
                : (m.video || m.media_url || undefined);
            setSharedData((prev) => ({ ...prev, mediaType, mediaFile: mediaFileToSet }));
            setSeparateImageFileByLanguage(prev => {
                const updated = { ...prev };
                delete updated[langCode];
                return updated;
            });
            return;
        }
    };

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        const newLang = locales[newValue].code;
        const prevLang = currentLang;

        const { title, description } = getValues();
        const updated = {
            ...formDataRef.current,
            [prevLang]: {
                title: title ?? formDataRef.current[prevLang]?.title ?? "",
                description: description ?? formDataRef.current[prevLang]?.description ?? "",
            },
        };
        formDataRef.current = updated;
        setFormData(updated);

        const nextValues = updated[newLang] || { title: "", description: "" };
        isTabSwitchingRef.current = true;
        setTabIndex(newValue);
        reset(nextValues);
        setTimeout(() => (isTabSwitchingRef.current = false), 0);

        // hydrate edit media for this language
        setMediaForLanguage(newLang, sharedData.mediaType || null, existingMediaByLang);
    };

    useEffect(() => {
        if (isTabSwitchingRef.current) return;
        const updated = { ...formDataRef.current, [currentLang]: { title: watchedTitle, description: watchedDescription } };
        formDataRef.current = updated;
        setFormData(updated);
    }, [watchedTitle, watchedDescription, currentLang]);

    useEffect(() => {
        if (!landingPageData) {
            setSnackbar({
                open: true,
                message: "Missing landing page data. Please open Edit from the list page.",
                severity: "error",
            });
            return;
        }

        const data = landingPageData;
        const effectiveId = Number(data?.content_id || data?.id || contentId) || null;
        if (effectiveId) setObjectId(effectiveId);

        const contents = data?.contents || {};
        const nextFormData: Record<string, LanguageFormData> = locales.reduce((acc, lang) => {
            const key = `content_${lang.code}`;
            const c = contents?.[key] || {};
            acc[lang.code] = { title: c?.title || "", description: c?.description || "" };
            return acc;
        }, {} as Record<string, LanguageFormData>);

        const perLangMedia: ExistingMediaByLang = locales.reduce((acc, lang) => {
            const key = `content_${lang.code}`;
            const c = contents?.[key] || {};
            acc[lang.code] = {
                image: c?.image,
                tts: c?.tts,
                audio: c?.audio,
                video: c?.video,
                media_url: c?.media_url,
            };
            return acc;
        }, {} as ExistingMediaByLang);

        // derive global mediaType if not present
        let derivedType: SharedFormData["mediaType"] = data?.mediaType || data?.media_type || null;
        if (!derivedType) {
            const first = locales.find((l) => {
                const m = perLangMedia[l.code];
                return !!(m?.tts || m?.audio || m?.video || m?.media_url || m?.image);
            });
            if (first) {
                const m = perLangMedia[first.code];
                if (m.tts) derivedType = "tts";
                else if (m.audio || (m.media_url && /audio_/i.test(m.media_url))) derivedType = "audio";
                else if (m.video || (m.media_url && /video_/i.test(m.media_url))) derivedType = "video";
                else if (m.image) derivedType = "image";
            }
        }

        setExistingMediaByLang(perLangMedia);
        setFormData(nextFormData);
        formDataRef.current = nextFormData;

        const nextShared: SharedFormData = {
            mediaType: derivedType,
            mediaFile: undefined,
            module: data?.module || "",
            module_id: data?.module_id || "",
            submodule_id: data?.submodule_id || "",
            content_name: data?.content_name || "",
        };

        setSharedData(nextShared);
        resetSharedForm({
            module: nextShared.module,
            module_id: nextShared.module_id,
            submodule_id: nextShared.submodule_id,
            mediaType: nextShared.mediaType,
            content_name: nextShared.content_name,
        });

        reset(nextFormData[currentLang]);
        setMediaForLanguage(currentLang, derivedType, perLangMedia);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [landingPageData]);

    const onSubmitAll = () => {
        console.log("sharedData?.mediaType >>",sharedData?.mediaType);
        
        setIsLoading(true);

        const isEmptyRichText = (html: string = ""): boolean => {
            const plainText = html.replace(/<[^>]+>/g, "").trim();
            return plainText === "";
        };

        if (!formDataRef.current["en"]?.title) {
            setIsLoading(false);
            setSnackbar({ open: true, message: `Title cannot be empty for english`, severity: "error" });
            return;
        }
        if (isEmptyRichText(formDataRef.current["en"]?.description)) {
            setIsLoading(false);
            setSnackbar({ open: true, message: `Description cannot be empty for english`, severity: "error" });
            return;
        }

        for (const lang of locales) {
            const title = formDataRef.current[lang.code]?.title?.trim();
            const description = formDataRef.current[lang.code]?.description;
            const hasTitle = !!title;
            const hasDescription = !isEmptyRichText(description);
            if (!hasTitle && !hasDescription) continue;
            if (!hasTitle) {
                setIsLoading(false);
                setSnackbar({ open: true, message: `Title cannot be empty for ${lang.name}`, severity: "error" });
                return;
            }
            if (!hasDescription) {
                setIsLoading(false);
                setSnackbar({ open: true, message: `Description cannot be empty for ${lang.name}`, severity: "error" });
                return;
            }
        }

        if (sharedData?.module && !sharedData?.module_id) {
            if (sharedData?.module && !exceptedModules.includes(sharedData?.module)) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Please add deeplink ${sharedData?.module === "EXTERNAL" ? " URL" : " ID"}`,
                    severity: "error",
                });
                return;
            }
        }

        // Edit: allow keeping existing media; only validate required media if nothing exists and nothing uploaded
        if (sharedData?.mediaType === "image") {
            const hasNew = Object.keys(imageFilesByLanguage).length > 0;
            const hasExisting = locales.some((l) => !!existingMediaByLang?.[l.code]?.image);
            if (!hasNew && !hasExisting) {
                setIsLoading(false);
                setSnackbar({ open: true, message: "Please select an image file for at least one language.", severity: "error" });
                return;
            }
        }

        setIsLoading(false);
        setConfirmDialogOpen(true);
    };

    const handleFinalSubmit = async () => {
        try {
            setIsUploadingMedia(true);

            const currentCampaignId = ObjectId || (contentId as any);
            if (!currentCampaignId) throw new Error("Missing content id");

            const getContentType = (file: File): string => file.type || "application/octet-stream";

            const uploadToSignedUrl = async (fileName: string, contentType: string, file: File) => {
                const signedUrlResponse: any = await getSignedUrl({
                    fileName,
                    bucketType: "landingPage",
                    contentType,
                    ObjectId: currentCampaignId.toString(),
                });
                const signedUrl =
                    signedUrlResponse?.data?.data?.signed_url ||
                    signedUrlResponse?.data?.data?.url ||
                    signedUrlResponse?.data?.signedUrl ||
                    signedUrlResponse?.data?.url;
                if (!signedUrl) throw new Error("Failed to get signed URL");
                const uploadResponse = await fetch(signedUrl, {
                    method: "PUT",
                    body: await file.arrayBuffer(),
                    headers: { "Content-Type": contentType },
                });
                if (!uploadResponse.ok) throw new Error("Failed to upload file");
            };

            const uploaded: {
                image: Record<string, string>;
                tts: Record<string, string>;
                audio: Record<string, string>;
                video: Record<string, string>;
            } = { image: {}, tts: {}, audio: {}, video: {} };

            if (sharedData?.mediaType === "image") {
                for (const [langCode, file] of Object.entries(imageFilesByLanguage)) {
                    const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
                    const fileName = `image_${langCode.toLowerCase()}.${ext}`;
                    await uploadToSignedUrl(fileName, getContentType(file), file);
                    uploaded.image[langCode] = fileName;
                }
            }

            if (sharedData?.mediaType === "tts") {
                for (const [langCode, file] of Object.entries(ttsFilesByLanguage)) {
                    const ext = (file.name.split(".").pop() || "mp3").toLowerCase();
                    const fileName = `tts_audio_${langCode.toLowerCase()}.${ext}`;
                    await uploadToSignedUrl(fileName, file.type || "audio/mpeg", file);
                    uploaded.tts[langCode] = fileName;
                }
            }

            if (sharedData?.mediaType === "audio") {
                for (const [langCode, file] of Object.entries(audioFilesByLanguage)) {
                    const ext = (file.name.split(".").pop() || "mp3").toLowerCase();
                    const fileName = `audio_${langCode.toLowerCase()}.${ext}`;
                    await uploadToSignedUrl(fileName, file.type || "audio/mpeg", file);
                    uploaded.audio[langCode] = fileName;
                }
            }

            if (sharedData?.mediaType === "video") {
                for (const [langCode, file] of Object.entries(videoFilesByLanguage)) {
                    const ext = (file.name.split(".").pop() || "mp4").toLowerCase();
                    const fileName = `video_${langCode.toLowerCase()}.${ext}`;
                    await uploadToSignedUrl(fileName, file.type || "video/mp4", file);
                    uploaded.video[langCode] = fileName;
                }
            }

            // Secondary image for audio/tts - per language
            const uploadedSecondaryImageFileNamesByLang: Record<string, string> = {};
            if (sharedData?.mediaType === "audio" || sharedData?.mediaType === "tts") {
                for (const [langCode, separateImageFile] of Object.entries(separateImageFileByLanguage)) {
                    if (!separateImageFile) continue;
                    // IMPORTANT (edit): only include `image` in payload if user uploaded a NEW image.
                    // If `separateImageFile` is a string (existing), treat it as unchanged -> omit from payload.
                    if (separateImageFile instanceof File) {
                        const imageFile = separateImageFile;
                        const imageExt = (imageFile.name.split(".").pop() || "jpg").toLowerCase();
                        const imageFileName = `landing_page_image_${langCode.toLowerCase()}.${imageExt}`;
                        await uploadToSignedUrl(imageFileName, imageFile.type || "image/jpeg", imageFile);
                        uploadedSecondaryImageFileNamesByLang[langCode] = imageFileName;
                    }
                }
            }


            let mediaTypeToSend = null;

            const contentPayload = locales.reduce((acc: any, lang: any) => {
                const langCode = lang.code;
                const { title, description } = formDataRef.current[langCode] || {};
                const hasContent = title?.trim() || (description || "").trim();
                if (!hasContent) return acc;

                // Always include textual fields; include media keys ONLY if updated in this edit session
                const contentForLang: any = { title, description };

                if (sharedData?.mediaType === "image") {
                    if (uploaded.image[langCode]) {
                        contentForLang.image = uploaded.image[langCode];
                        mediaTypeToSend = 'image';
                    }
                }

                if (sharedData?.mediaType === "tts") {
                    if (uploaded.tts[langCode]) {
                        contentForLang.tts = uploaded.tts[langCode];
                    }
                    const secondaryImageForLang = uploadedSecondaryImageFileNamesByLang[langCode];
                    if (secondaryImageForLang) {
                        contentForLang.image = secondaryImageForLang;
                        mediaTypeToSend = 'image';
                    }
                }

                if (sharedData?.mediaType === "audio") {
                    if (uploaded.audio[langCode]) {
                        contentForLang.audio = uploaded.audio[langCode];
                    }
                    const secondaryImageForLang = uploadedSecondaryImageFileNamesByLang[langCode];
                    if (secondaryImageForLang) {
                        contentForLang.image = secondaryImageForLang;
                    }
                }

                if (sharedData?.mediaType === "video") {
                    if (uploaded.video[langCode]) {
                        contentForLang.video = uploaded.video[langCode];
                    }
                }

                acc[`content_${langCode}`] = contentForLang;
                return acc;
            }, {});

            const payload: any = {
                content_name: sharedData.content_name || "",
                contents: contentPayload,
                module: sharedData.module,
                module_id: sharedData.module_id,
                submodule_id: sharedData.submodule_id,
                media_type: sharedData.mediaType,
            };
            if(!payload['module']){
                delete payload['module'];
            }
            if(!payload['module_id']){
                delete payload['module_id'];
            }
            if(!payload['submodule_id']){
                delete payload['submodule_id'];
            }

            await updateLandingPage(landingPageData.id, payload);

            setSnackbar({ open: true, message: "Content updated successfully!", severity: "success" });
            setConfirmDialogOpen(false);
            navigate("/landing-page");
        } catch (e: any) {
            console.error("Edit submission failed:", e);
            setSnackbar({ open: true, message: e?.message || "Something went wrong.", severity: "error" });
        } finally {
            setIsUploadingMedia(false);
            setIsLoading(false);
        }
    };

    const filledLanguages = locales
        .filter((lang) => formDataRef.current[lang.code]?.title?.trim() && (formDataRef.current[lang.code]?.description || "").trim())
        .map((lang) => lang.name);

    return (
        <MainLayout>
            <Card sx={{ width: "100%", borderRadius: 2, m: 1 }} elevation={1}>
                <CardContent>
                    <Box display="flex" flexDirection="row">
                        <Box width={400} display="flex" alignItems="center">
                            <IconButton onClick={() => navigate(-1)}>
                                <ArrowBack />
                            </IconButton>
                            <Typography variant="h6" fontWeight="600" paddingLeft={1}>
                                Edit Content
                            </Typography>
                        </Box>
                    </Box>

                    <Tabs value={tabIndex} onChange={handleTabChange}>
                        {locales.map((locale) => (
                            <Tab key={locale.code} label={locale.name} />
                        ))}
                    </Tabs>

                    <Divider sx={{ mb: 2 }} />

                    {currentLang === "en" && <Controller
                        name="content_name"
                        control={sharedControl}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Content Name"
                                margin="normal"
                                onChange={(e) => {
                                    field.onChange(e);
                                    setSharedData((prev) => ({ ...prev, content_name: e.target.value }));
                                }}
                            />
                        )}
                    />}

                    <Controller
                        name="title"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={
                                    currentLang === "en" ? (
                                        <>
                                            Title <span style={{ color: "red" }}>*</span>
                                        </>
                                    ) : (
                                        "Title"
                                    )
                                }
                                margin="normal"
                            />
                        )}
                    />

                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <HTMLEditor
                                value={field.value}
                                onChange={field.onChange}
                                placeholder={`Enter Description ${currentLang === "en" ? "*" : ""}`}
                                readOnly={false}
                            />
                        )}
                    />
                    {currentLang === "en" && (
                        <>


                            <Controller
                                name="module"
                                control={sharedControl}
                                render={({ field }) => (
                                    <TextField
                                        select
                                        label="Select Module Type"
                                        fullWidth
                                        margin="normal"
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setSharedData((prev) => ({ ...prev, module: e.target.value }));
                                        }}
                                    >
                                        {moduleTypes.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />

                            {!exceptedModules.includes(sharedData?.module || "") && (
                                <Controller
                                    name="module_id"
                                    control={sharedControl}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label={sharedData.module === "EXTERNAL" ? "Deeplink URL" : "Module ID"}
                                            margin="normal"
                                            onChange={(e) => {
                                                field.onChange(e);
                                                setSharedData((prev) => ({ ...prev, module_id: e.target.value }));
                                            }}
                                        />
                                    )}
                                />
                            )}

                            {sharedData?.module === "SERVICES" && (
                                <Controller
                                    name="submodule_id"
                                    control={sharedControl}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Submodule ID"
                                            margin="normal"
                                            onChange={(e) => {
                                                field.onChange(e);
                                                setSharedData((prev) => ({ ...prev, submodule_id: e.target.value }));
                                            }}
                                        />
                                    )}
                                />
                            )}
                        </>
                    )}


                    <MediaUploadEdit
                        tabIndex={tabIndex}
                        mediaType={sharedData.mediaType || null}
                        setSharedData={setSharedData}
                        mediaFile={sharedData.mediaFile}
                        control={sharedControl}
                        language={currentLang}
                        onMediaTypeChange={handleMediaTypeChange}
                        onMediaFileChange={handleMediaFileChange}
                        onUploadSuccess={() => { }}
                        onError={handleMediaUploadError}
                        onTtsGenerated={(langCode, file) => setTtsFilesByLanguage((p) => ({ ...p, [langCode]: file }))}
                        onAudioFileChange={(langCode, file) => setAudioFilesByLanguage((p) => ({ ...p, [langCode]: file }))}
                        onVideoFileChange={(langCode, file) => setVideoFilesByLanguage((p) => ({ ...p, [langCode]: file }))}
                        onImageFileChange={handleImageFileChange}
                        imageFile={separateImageFileByLanguage[currentLang]}
                        onImageFileChangeSecondary={handleSeparateImageFileChange}
                    />

                    {currentLang === "en" && (
                        <Box sx={{ display: "flex", justifyContent: "end", alignItems: "center", gap: 2 }}>
                            {isUploadingMedia && (
                                <Typography variant="body2" color="text.secondary">
                                    Uploading media...
                                </Typography>
                            )}
                            <Button
                                disabled={isLoading || isUploadingMedia}
                                onClick={onSubmitAll}
                                variant="contained"
                                color="primary"
                                sx={{ mt: 2 }}
                                startIcon={isUploadingMedia ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {isUploadingMedia ? "Uploading Media..." : "Update Content"}
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <ConfirmLanguagesDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={handleFinalSubmit}
                filledLanguages={filledLanguages}
                isLoading={isLoading || isUploadingMedia}
                isEdit={true}
            />

            <SnackbarComp
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </MainLayout>
    );
};

